import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * API Service for backend communication
 */

export const api = {
  /**
   * Convert multiple PDF files to Markdown
   * @param {FileList} files - PDF files to convert
   * @returns {Promise} - Response with markdown files
   */
  convertMultipleFiles: async (files) => {
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    const response = await axios.post(`${API_BASE_URL}/convert-multiple`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 600000 // 10 minutes for multiple files
    });

    return response.data;
  },

  /**
   * Validate documents using AI
   * @param {Object} requestData - Request metadata from Step 1
   * @param {Array} markdownFiles - Converted markdown files
   * @returns {Promise} - Validation report
   */
  validateDocuments: async (requestData, markdownFiles) => {
    const response = await axios.post(`${API_BASE_URL}/validate`, {
      requestData,
      markdownFiles
    }, {
      timeout: 600000 // 10 minutes for AI validation
    });

    return response.data;
  },

  /**
   * Request segmentation for a document
   * @param {string} requestId - Datalab request ID
   * @param {string} fileName - Original file name
   * @returns {Promise} - Segmentation results
   */
  requestSegmentation: async (requestId, fileName) => {
    const response = await axios.post(`${API_BASE_URL}/segmentation`, {
      requestId,
      fileName
    }, {
      timeout: 120000 // 2 minutes
    });

    return response.data;
  },

  /**
   * Fetch page thumbnails from Datalab API
   * @param {string} requestId - Datalab request ID
   * @param {number} thumbWidth - Thumbnail width (default 800)
   * @returns {Promise} - Thumbnails data
   */
  fetchThumbnails: async (requestId, thumbWidth = 800) => {
    const response = await axios.get(`${API_BASE_URL}/thumbnails/${requestId}`, {
      params: { thumbWidth },
      timeout: 60000 // 1 minute
    });

    return response.data;
  },

  /**
   * Fetch cached report by requestId (no upload or AI call)
   * @param {string} requestId - Datalab request ID
   * @returns {Promise} - Cached report data
   */
  fetchCachedReport: async (requestId) => {
    const response = await axios.get(`${API_BASE_URL}/report/${requestId}`, {
      timeout: 30000 // 30 seconds
    });

    return response.data;
  },

  /**
   * Save user preferences for a report (display mode, active tab)
   * @param {string} requestId - Datalab request ID
   * @param {Object} preferences - { displayMode, activeTab }
   * @returns {Promise} - Success response
   */
  saveReportPreferences: async (requestId, preferences) => {
    const response = await axios.put(`${API_BASE_URL}/report/${requestId}/preferences`, preferences, {
      timeout: 5000 // 5 seconds
    });

    return response.data;
  },

  /**
   * Chat with document data
   * @param {string} question - User's question
   * @param {string} markdownContent - Document markdown content
   * @param {Array} conversationHistory - Previous conversation messages (optional)
   * @param {Array} imageList - List of images from document (optional)
   * @param {Array} segments - List of document segments (optional)
   * @returns {Promise} - Chat response with answer and evidence
   */
  chatWithDocument: async (question, markdownContent, conversationHistory = [], imageList = [], segments = []) => {
    const response = await axios.post(`${API_BASE_URL}/chat`, {
      question,
      markdownContent,
      conversationHistory,
      imageList,
      segments
    }, {
      timeout: 120000 // 2 minutes for chat
    });

    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await axios.get('/health');
    return response.data;
  }
};

export default api;
